import { defineAsyncComponent } from '../../lib/utils/vue3compat/vue.js?vue3';
import Participants from '../../sidebar/components/participants/participants.vue?vue3';
import glFeatureFlagMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import { ListType } from '~/boards/constants';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import WorkItemDates from '../../../../../ee/app/assets/javascripts/work_items/components/work_item_dates.vue?vue3';
import { WORK_ITEM_TYPE_NAME_EPIC, STATE_CLOSED } from '../constants';
import { findAssigneesWidget, findColorWidget, findCrmContactsWidget, findCustomFieldsWidget, findHealthStatusWidget, findHierarchyWidget, findHierarchyWidgetDefinition, findIterationWidget, findLabelsWidget, findMilestoneWidget, findParticipantsWidget, findProgressWidget, findRequesterWidget, findStartAndDueDateWidget, findStatusWidget, findTimeTrackingWidget, findWeightWidget } from '../utils.js?vue3';
import workItemParticipantsQuery from '../graphql/work_item_participants.query.graphql';
import workItemAllowedParentTypesQuery from '../graphql/work_item_allowed_parent_types.query.graphql';
import WorkItemAssignees from './work_item_assignees.vue?vue3';
import WorkItemLabels from './work_item_labels.vue?vue3';
import WorkItemMilestone from './work_item_milestone.vue?vue3';
import WorkItemParent from './work_item_parent.vue?vue3';
import WorkItemRequester from './work_item_requester.vue?vue3';
import WorkItemTimeTracking from './work_item_time_tracking.vue?vue3';
import WorkItemCrmContacts from './work_item_crm_contacts.vue?vue3';
export default {
    name: 'WorkItemAttributesWrapper',
    ListType,
    components: {
        Participants,
        WorkItemLabels,
        WorkItemMilestone,
        WorkItemAssignees,
        WorkItemParent,
        WorkItemRequester,
        WorkItemTimeTracking,
        WorkItemCrmContacts,
        WorkItemDates,
        WorkItemWeight: defineAsyncComponent(()=>import('../../../../../ee/app/assets/javascripts/work_items/components/work_item_weight.vue?vue3')),
        WorkItemProgress: defineAsyncComponent(()=>import('../../../../../ee/app/assets/javascripts/work_items/components/work_item_progress.vue?vue3')),
        WorkItemIteration: defineAsyncComponent(()=>import('../../../../../ee/app/assets/javascripts/work_items/components/work_item_iteration.vue?vue3')),
        WorkItemHealthStatus: defineAsyncComponent(()=>import('../../../../../ee/app/assets/javascripts/work_items/components/work_item_health_status.vue?vue3')),
        WorkItemColor: defineAsyncComponent(()=>import('../../../../../ee/app/assets/javascripts/work_items/components/work_item_color.vue?vue3')),
        WorkItemCustomFields: defineAsyncComponent(()=>import('../../../../../ee/app/assets/javascripts/work_items/components/work_item_custom_fields.vue?vue3')),
        WorkItemStatus: defineAsyncComponent(()=>import('../../../../../ee/app/assets/javascripts/work_items/components/work_item_status.vue?vue3'))
    },
    mixins: [
        glFeatureFlagMixin()
    ],
    inject: {
        hasSubepicsFeature: {
            default: false
        }
    },
    props: {
        fullPath: {
            type: String,
            required: true
        },
        workItem: {
            type: Object,
            required: true
        },
        groupPath: {
            type: String,
            required: false,
            default: ''
        },
        isGroup: {
            type: Boolean,
            required: true
        }
    },
    emits: [
        'attributes-updated',
        'error'
    ],
    data () {
        return {
            workItemParticipants: {},
            allowedParentTypes: []
        };
    },
    apollo: {
        workItemParticipants: {
            query: workItemParticipantsQuery,
            variables () {
                return {
                    fullPath: this.fullPath,
                    iid: this.workItem.iid,
                    useWorkItemFeatures: Boolean(this.glFeatures?.workItemFeaturesField)
                };
            },
            skip () {
                return !this.workItem.iid;
            },
            update ({ namespace }) {
                if (!namespace?.workItem) return {};
                return findParticipantsWidget(namespace.workItem)?.participants || {};
            },
            error (e) {
                Sentry.captureException(e);
            }
        },
        allowedParentTypes: {
            query: workItemAllowedParentTypesQuery,
            variables () {
                return {
                    id: this.workItem.id
                };
            },
            update (data) {
                return findHierarchyWidgetDefinition(data.workItem)?.allowedParentTypes?.nodes ?? [];
            },
            error (e) {
                Sentry.captureException(e);
            }
        }
    },
    computed: {
        workItemType () {
            return this.workItem.workItemType?.name;
        },
        canUpdateMetadata () {
            return this.workItem?.userPermissions?.setWorkItemMetadata;
        },
        canAdminWorkItemLink () {
            return this.workItem?.userPermissions?.adminWorkItemLink;
        },
        canUpdateParent () {
            return this.canUpdateMetadata || this.canAdminWorkItemLink;
        },
        workItemParticipantNodes () {
            return this.workItemParticipants.nodes || [];
        },
        workItemParticipantCount () {
            return this.workItemParticipants.count || 0;
        },
        workItemAssignees () {
            return findAssigneesWidget(this.workItem);
        },
        workItemLabels () {
            return findLabelsWidget(this.workItem);
        },
        workItemRequester () {
            return findRequesterWidget(this.workItem);
        },
        workItemStatus () {
            return findStatusWidget(this.workItem);
        },
        workItemStartAndDueDate () {
            return findStartAndDueDateWidget(this.workItem);
        },
        canWorkItemRollUp () {
            return this.workItemType === WORK_ITEM_TYPE_NAME_EPIC;
        },
        workItemWeight () {
            return findWeightWidget(this.workItem);
        },
        workItemProgress () {
            return findProgressWidget(this.workItem);
        },
        workItemIteration () {
            return findIterationWidget(this.workItem);
        },
        workItemHealthStatus () {
            return findHealthStatusWidget(this.workItem);
        },
        workItemHierarchy () {
            return findHierarchyWidget(this.workItem);
        },
        workItemMilestone () {
            return findMilestoneWidget(this.workItem);
        },
        isParentEnabled () {
            return this.workItemType === WORK_ITEM_TYPE_NAME_EPIC ? this.hasSubepicsFeature : true;
        },
        workItemParent () {
            return findHierarchyWidget(this.workItem)?.parent;
        },
        showParent () {
            return this.allowedParentTypes.length > 0 && this.workItemHierarchy && this.isParentEnabled;
        },
        workItemTimeTracking () {
            return findTimeTrackingWidget(this.workItem);
        },
        workItemColor () {
            return findColorWidget(this.workItem);
        },
        hasParent () {
            return this.workItemHierarchy?.hasParent;
        },
        isWorkItemClosed () {
            return this.workItem.state === STATE_CLOSED;
        },
        workItemCrmContacts () {
            const crmContactsWidget = findCrmContactsWidget(this.workItem);
            return crmContactsWidget && crmContactsWidget.contactsAvailable ? crmContactsWidget : null;
        },
        customFields () {
            return findCustomFieldsWidget(this.workItem)?.customFieldValues;
        }
    }
};
